import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { 
  Zap, 
  Settings, 
  Calendar, 
  Mail, 
  MessageSquare, 
  BarChart3, 
  Database,
  Webhook,
  Key,
  CheckCircle,
  AlertCircle,
  ExternalLink
} from 'lucide-react';

interface Integration {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: React.ReactNode;
  status: 'available' | 'connected' | 'disconnected' | 'error';
  requiresConfig: boolean;
  configFields?: {
    name: string;
    label: string;
    type: 'text' | 'password' | 'url' | 'select';
    required: boolean;
    options?: string[];
  }[];
}

const AVAILABLE_INTEGRATIONS: Integration[] = [
  {
    id: 'slack',
    name: 'Slack',
    description: 'Send notifications and updates to Slack channels',
    category: 'Communication',
    icon: <MessageSquare className="h-5 w-5" />,
    status: 'available',
    requiresConfig: true,
    configFields: [
      { name: 'webhook_url', label: 'Webhook URL', type: 'url', required: true },
      { name: 'channel', label: 'Default Channel', type: 'text', required: false }
    ]
  },
  {
    id: 'zapier',
    name: 'Zapier',
    description: 'Automate workflows with 3000+ apps',
    category: 'Automation',
    icon: <Zap className="h-5 w-5" />,
    status: 'available',
    requiresConfig: true,
    configFields: [
      { name: 'webhook_url', label: 'Zapier Webhook URL', type: 'url', required: true }
    ]
  },
  {
    id: 'google-calendar',
    name: 'Google Calendar',
    description: 'Schedule strategy sessions and reminders',
    category: 'Productivity',
    icon: <Calendar className="h-5 w-5" />,
    status: 'available',
    requiresConfig: true,
    configFields: [
      { name: 'calendar_id', label: 'Calendar ID', type: 'text', required: true },
      { name: 'api_key', label: 'API Key', type: 'password', required: true }
    ]
  },
  {
    id: 'microsoft-teams',
    name: 'Microsoft Teams',
    description: 'Share documents and updates in Teams',
    category: 'Communication',
    icon: <MessageSquare className="h-5 w-5" />,
    status: 'available',
    requiresConfig: true,
    configFields: [
      { name: 'webhook_url', label: 'Teams Webhook URL', type: 'url', required: true }
    ]
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    description: 'Sync business data with Salesforce CRM',
    category: 'CRM',
    icon: <Database className="h-5 w-5" />,
    status: 'available',
    requiresConfig: true,
    configFields: [
      { name: 'instance_url', label: 'Salesforce Instance URL', type: 'url', required: true },
      { name: 'access_token', label: 'Access Token', type: 'password', required: true }
    ]
  },
  {
    id: 'hubspot',
    name: 'HubSpot',
    description: 'Integrate with HubSpot CRM and marketing tools',
    category: 'CRM',
    icon: <BarChart3 className="h-5 w-5" />,
    status: 'available',
    requiresConfig: true,
    configFields: [
      { name: 'api_key', label: 'HubSpot API Key', type: 'password', required: true }
    ]
  },
  {
    id: 'custom-webhook',
    name: 'Custom Webhook',
    description: 'Send data to any custom endpoint',
    category: 'Developer',
    icon: <Webhook className="h-5 w-5" />,
    status: 'available',
    requiresConfig: true,
    configFields: [
      { name: 'webhook_url', label: 'Webhook URL', type: 'url', required: true },
      { name: 'headers', label: 'Custom Headers (JSON)', type: 'text', required: false },
      { name: 'auth_type', label: 'Authentication', type: 'select', required: false, options: ['none', 'bearer', 'api_key'] }
    ]
  }
];

interface EnterpriseIntegrationsProps {
  organizationId: string;
}

export const EnterpriseIntegrations = ({ organizationId }: EnterpriseIntegrationsProps) => {
  const [integrations, setIntegrations] = useState<Integration[]>(AVAILABLE_INTEGRATIONS);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [configData, setConfigData] = useState<Record<string, string>>({});
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadIntegrationsStatus();
  }, [organizationId]);

  const loadIntegrationsStatus = async () => {
    try {
      // Load existing integration configurations from organization settings
      const { data: organization, error } = await supabase
        .from('organizations')
        .select('branding')
        .eq('id', organizationId)
        .single();

      if (error) throw error;

      // Update integration statuses based on stored configurations
      const integrationConfigs = (organization.branding as any)?.integrations || {};
      
      setIntegrations(prev => prev.map(integration => ({
        ...integration,
        status: integrationConfigs[integration.id] ? 'connected' : 'available'
      })));
    } catch (error) {
      console.error('Error loading integrations:', error);
    }
  };

  const handleConfigure = (integration: Integration) => {
    setSelectedIntegration(integration);
    setConfigData({});
    setIsConfiguring(true);
  };

  const handleSaveConfiguration = async () => {
    if (!selectedIntegration) return;

    try {
      setIsLoading(true);

      // Validate required fields
      const missingFields = selectedIntegration.configFields?.filter(field => 
        field.required && !configData[field.name]
      ) || [];

      if (missingFields.length > 0) {
        throw new Error(`Please fill in required fields: ${missingFields.map(f => f.label).join(', ')}`);
      }

      // Get current organization data
      const { data: organization, error: fetchError } = await supabase
        .from('organizations')
        .select('branding')
        .eq('id', organizationId)
        .single();

      if (fetchError) throw fetchError;

      // Update organization with integration config
      const currentBranding = (organization.branding as any) || {};
      const integrations = currentBranding.integrations || {};

      integrations[selectedIntegration.id] = {
        ...configData,
        enabled: true,
        configured_at: new Date().toISOString()
      };

      const { error: updateError } = await supabase
        .from('organizations')
        .update({
          branding: {
            ...currentBranding,
            integrations
          }
        })
        .eq('id', organizationId);

      if (updateError) throw updateError;

      // Test the integration
      await testIntegration(selectedIntegration.id, configData);

      setIntegrations(prev => prev.map(integration => 
        integration.id === selectedIntegration.id 
          ? { ...integration, status: 'connected' }
          : integration
      ));

      toast({
        title: "Integration Configured",
        description: `${selectedIntegration.name} has been successfully configured and tested.`,
      });

      setIsConfiguring(false);
      setSelectedIntegration(null);
    } catch (error: any) {
      console.error('Error configuring integration:', error);
      toast({
        title: "Configuration Error",
        description: error.message || "Failed to configure integration",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const testIntegration = async (integrationId: string, config: Record<string, string>) => {
    try {
      // Test the integration by sending a test payload
      const { data, error } = await supabase.functions.invoke('test-integration', {
        body: {
          integrationId,
          config,
          organizationId,
          testMessage: `Test connection from CSO Agent at ${new Date().toISOString()}`
        }
      });

      if (error) throw error;
      
      return data;
    } catch (error) {
      console.error('Integration test failed:', error);
      throw new Error('Integration test failed - please check your configuration');
    }
  };

  const handleToggleIntegration = async (integrationId: string, enabled: boolean) => {
    try {
      const { data: organization, error: fetchError } = await supabase
        .from('organizations')
        .select('branding')
        .eq('id', organizationId)
        .single();

      if (fetchError) throw fetchError;

      const currentBranding = (organization.branding as any) || {};
      const integrations = currentBranding.integrations || {};

      if (integrations[integrationId]) {
        integrations[integrationId].enabled = enabled;

        const { error: updateError } = await supabase
          .from('organizations')
          .update({
            branding: {
              ...currentBranding,
              integrations
            }
          })
          .eq('id', organizationId);

        if (updateError) throw updateError;

        setIntegrations(prev => prev.map(integration => 
          integration.id === integrationId 
            ? { ...integration, status: enabled ? 'connected' : 'disconnected' }
            : integration
        ));

        toast({
          title: enabled ? "Integration Enabled" : "Integration Disabled",
          description: `${integrations[integrationId].name || integrationId} has been ${enabled ? 'enabled' : 'disabled'}.`,
        });
      }
    } catch (error: any) {
      console.error('Error toggling integration:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to toggle integration",
        variant: "destructive",
      });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'disconnected': return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      case 'error': return <AlertCircle className="h-4 w-4 text-red-600" />;
      default: return <Settings className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected': return 'bg-green-100 text-green-800';
      case 'disconnected': return 'bg-yellow-100 text-yellow-800';
      case 'error': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const categories = Array.from(new Set(integrations.map(i => i.category)));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Enterprise Integrations</h2>
          <p className="text-gray-600">Connect CSO Agent with your existing business tools and workflows</p>
        </div>
        <Badge variant="secondary" className="bg-purple-100 text-purple-800">
          Enterprise Feature
        </Badge>
      </div>

      {categories.map(category => (
        <Card key={category}>
          <CardHeader>
            <CardTitle className="text-lg">{category}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {integrations
                .filter(integration => integration.category === category)
                .map(integration => (
                  <div key={integration.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-100 rounded-lg">
                          {integration.icon}
                        </div>
                        <div>
                          <h3 className="font-medium">{integration.name}</h3>
                          <Badge className={`text-xs ${getStatusColor(integration.status)}`}>
                            {integration.status}
                          </Badge>
                        </div>
                      </div>
                      {getStatusIcon(integration.status)}
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-4">{integration.description}</p>
                    
                    <div className="flex items-center justify-between">
                      {integration.status === 'connected' ? (
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={integration.status === 'connected'}
                            onCheckedChange={(enabled) => handleToggleIntegration(integration.id, enabled)}
                          />
                          <span className="text-sm">Active</span>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => handleConfigure(integration)}
                          disabled={!integration.requiresConfig}
                        >
                          <Key className="h-3 w-3 mr-1" />
                          Configure
                        </Button>
                      )}
                      
                      <Button variant="ghost" size="sm">
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Configuration Dialog */}
      {isConfiguring && selectedIntegration && (
        <Card className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <CardHeader>
              <CardTitle>Configure {selectedIntegration.name}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedIntegration.configFields?.map(field => (
                <div key={field.name}>
                  <Label htmlFor={field.name}>{field.label}</Label>
                  {field.type === 'select' ? (
                    <select
                      id={field.name}
                      value={configData[field.name] || ''}
                      onChange={(e) => setConfigData(prev => ({ ...prev, [field.name]: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Select...</option>
                      {field.options?.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      id={field.name}
                      type={field.type}
                      value={configData[field.name] || ''}
                      onChange={(e) => setConfigData(prev => ({ ...prev, [field.name]: e.target.value }))}
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                    />
                  )}
                </div>
              ))}
              
              <div className="flex gap-2 pt-4">
                <Button onClick={handleSaveConfiguration} disabled={isLoading}>
                  {isLoading ? 'Testing...' : 'Save & Test'}
                </Button>
                <Button variant="outline" onClick={() => setIsConfiguring(false)}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </div>
        </Card>
      )}
    </div>
  );
};